import { updateProfile, useAppDispatch, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import useApi from "@/components/useApi";
import { EmailChangeStep, MILLISECONDS_PER_SECOND, RESEND_COOLDOWN_SECONDS } from "@/pages/UpdateEmail/constants";
import useDeadlineCountdown from "@/pages/UpdateEmail/hooks/useDeadlineCountdown";
import { clearPendingEmailChange, readPendingEmailChange, writePendingEmailChange } from "@/pages/UpdateEmail/utils/emailChangePendingStorage";
import resolveEmailChangeErrorMessage from "@/pages/UpdateEmail/utils/resolveEmailChangeErrorMessage";
import type { ConfirmEmailChangeResponse, RequestEmailChangeResponse } from "@/types/api";

type EmailChangeFlowState = {
  step: EmailChangeStep;
  pendingEmail: string;
  expiresAt: number | null;
  resendAvailableAt: number | null;
};

const EMPTY_FLOW_STATE: EmailChangeFlowState = {
  step: EmailChangeStep.REQUEST,
  pendingEmail: "",
  expiresAt: null,
  resendAvailableAt: null,
};

/**
 * There is no endpoint to ask the server whether a change is pending, so a reload would otherwise
 * drop the user back to step one and force a new code — and the request endpoint only allows two
 * calls per minute. The snapshot is restored instead, without a resend cooldown: the last request
 * time is unknown after a reload, and being permissive costs at most one rate-limit message,
 * whereas being restrictive would block a legitimate resend.
 */
const buildInitialState = (): EmailChangeFlowState => {
  const pending = readPendingEmailChange();

  if (!pending || pending.expiresAt <= Date.now()) {
    clearPendingEmailChange();
    return EMPTY_FLOW_STATE;
  }

  return { ...EMPTY_FLOW_STATE, step: EmailChangeStep.CONFIRM, pendingEmail: pending.pendingEmail, expiresAt: pending.expiresAt };
};

const resolveExpiresAt = (expiresInSeconds: number): number | null => {
  const seconds = Number(expiresInSeconds);
  // The window is whatever the server says it is; an unusable value shows no countdown rather than
  // a wrong one.
  return Number.isFinite(seconds) && seconds > 0 ? Date.now() + seconds * MILLISECONDS_PER_SECOND : null;
};

const useEmailChangeFlow = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [stateFlow, setStateFlow] = useState<EmailChangeFlowState>(buildInitialState);

  const {
    jsonApiService,
    callApi: callRequestApi,
    isLoading: isRequestingCode,
    errorData: requestErrorData,
    resetError: resetRequestError,
  } = useApi<RequestEmailChangeResponse>();
  const {
    callApi: callConfirmApi,
    isLoading: isConfirmingCode,
    errorData: confirmErrorData,
    resetError: resetConfirmError,
  } = useApi<ConfirmEmailChangeResponse>();

  const secondsUntilExpiry = useDeadlineCountdown(stateFlow.expiresAt);
  const secondsUntilResend = useDeadlineCountdown(stateFlow.resendAvailableAt);

  const requestCode = useCallback(
    (email: string): void => {
      resetConfirmError();
      callRequestApi(jsonApiService.requestEmailChange, email.trim().toLowerCase())
        .then((result) => {
          if (!result?.pendingEmail) {
            return;
          }

          const expiresAt = resolveExpiresAt(result.expiresInSeconds);
          if (expiresAt) {
            writePendingEmailChange({ pendingEmail: result.pendingEmail, expiresAt });
          }

          setStateFlow({
            step: EmailChangeStep.CONFIRM,
            pendingEmail: result.pendingEmail,
            expiresAt,
            resendAvailableAt: Date.now() + RESEND_COOLDOWN_SECONDS * MILLISECONDS_PER_SECOND,
          });
        })
        .catch(() => {
          // The failure is already exposed through errorData, which drives the message under the form.
        });
    },
    [callRequestApi, jsonApiService, resetConfirmError],
  );

  const editEmail = useCallback((): void => {
    clearPendingEmailChange();
    setStateFlow((previous) => ({ ...previous, step: EmailChangeStep.REQUEST, expiresAt: null }));
  }, []);

  const confirmCode = useCallback(
    (code: string): void => {
      resetRequestError();
      callConfirmApi(jsonApiService.confirmEmailChange, code.trim())
        .then((result) => {
          if (!result?.email) {
            return;
          }

          clearPendingEmailChange();
          // AppLayout sends Hide My Email users back to this page while profile.email is still the
          // relay address. Write the confirmed email first so that redirect does not win the race.
          dispatch(updateProfile({ email: result.email }));
          navigate("/");
        })
        .catch(() => {
          // Attempts are counted by the server, which answers the ceiling with its own error code.
          // Counting here as well would burn an attempt on failures that are not wrong codes — a
          // taken address, an expired request, a rate limit — and eventually accuse the user of
          // guessing when they never did.
        });
    },
    [callConfirmApi, dispatch, jsonApiService, navigate, resetRequestError],
  );

  const resendCode = useCallback((): void => requestCode(stateFlow.pendingEmail), [requestCode, stateFlow.pendingEmail]);

  return {
    step: stateFlow.step,
    pendingEmail: stateFlow.pendingEmail,
    isRequestingCode,
    isConfirmingCode,
    hasExpired: stateFlow.expiresAt !== null && secondsUntilExpiry <= 0,
    secondsUntilExpiry: stateFlow.expiresAt === null ? null : secondsUntilExpiry,
    secondsUntilResend,
    requestErrorMessage: resolveEmailChangeErrorMessage({ errorData: requestErrorData, step: EmailChangeStep.REQUEST, t }),
    confirmErrorMessage:
      resolveEmailChangeErrorMessage({ errorData: confirmErrorData, step: EmailChangeStep.CONFIRM, t }) ??
      resolveEmailChangeErrorMessage({ errorData: requestErrorData, step: EmailChangeStep.REQUEST, t }),
    requestCode,
    confirmCode,
    resendCode,
    editEmail,
  };
};

export default useEmailChangeFlow;
